import {
  getMembershipPlanDisplayPricing,
  resolvePaymentCardBrand,
  type MarketedMembershipPlan,
} from '@frontend/api-client';
import type { Card } from '@frontend/api-types';
import { Visa } from '@frontend/assets';
import { formatCurrencyNatural, formatNumber, makeCent, pluralize } from '@frontend/duck-tape';
import { formatTaskEstimate, type PurchasePackage } from '@frontend/react-domain';
import { Button, Divider, Icon, Image, SkeletonList, Text, XStack, YStack } from '@frontend/web-react';
import { StripeProvider } from '@frontend/web/components';
import { usePricingTerminology } from '@frontend/web/context';
import { useListCardsQuery } from '@frontend/web/hooks';
import dayjs from 'dayjs';
import { BuyCreditsExpressCheckout } from './BuyCreditsExpressCheckout';

export type SelectPaymentProps = {
  onApplePayComplete?: () => void;
  onSelectCard: (card: Card) => void;
  paymentSchedule?: {
    amountDueToday: Cent;
    dueDate?: DateISOString;
  };
  promoCode?: string;
  promotionDescription?: string;
  purchasePackage: PurchasePackage;
  selectedPlanData?: MarketedMembershipPlan;
};

export const SelectPayment = ({
  onApplePayComplete,
  onSelectCard,
  paymentSchedule,
  promoCode,
  promotionDescription,
  purchasePackage,
  selectedPlanData,
}: SelectPaymentProps) => {
  const { pricingTerminology, showCredits } = usePricingTerminology();
  const { data: cards, isLoading: isCardsLoading } = useListCardsQuery();
  const defaultCard = cards?.find((card) => card.isDefault);
  const selectedPlanPricing = getMembershipPlanDisplayPricing(selectedPlanData);

  const basePrice =
    purchasePackage.type === 'credits'
      ? purchasePackage.package.priceCent
      : selectedPlanPricing?.primaryPrice ?? selectedPlanData?.price ?? makeCent(0);

  const price = purchasePackage.type === 'credits' ? basePrice : selectedPlanPricing?.primaryPrice ?? basePrice;
  const buyingSubscription = Boolean(purchasePackage.type === 'subscription');

  if (buyingSubscription && !selectedPlanData) {
    return (
      <YStack alignItems="center" className="py-xl" gap="md" justifyContent="center">
        <Icon className="text-red-500" name="IconAlertCircle" size={48} />
        <Text type="h5">Your plan details took a duck dive.</Text>
        <Text color="secondary" type="p-sm">
          {"Don't worry, we'll bring them back."}
        </Text>
      </YStack>
    );
  }

  return (
    <YStack className="py-sm" gap="md">
      <YStack className="px-md py-sm" gap="md">
        {purchasePackage.type === 'subscription' && selectedPlanData ? (
          <YStack gap="xs">
            <Text type="p-lg">Your membership plan</Text>
            <XStack
              alignItems="center"
              bg="white"
              borderColor="borderSecondary"
              borderWidth={2}
              className="justify-between"
              gapX="md"
              p="md"
              radius="lg"
            >
              <YStack>
                <Text type="h5">{selectedPlanData.label}</Text>
                <Text type="p-sm">
                  {showCredits
                    ? `${selectedPlanData.creditsPerMonth} ${pluralize(pricingTerminology.unit_term, selectedPlanData.creditsPerMonth ?? 0)} ~ ${selectedPlanData.estimatedTasksPerMonth.min} to ${selectedPlanData.estimatedTasksPerMonth.max} tasks`
                    : `${selectedPlanData.estimatedTasksPerMonth.min} to ${selectedPlanData.estimatedTasksPerMonth.max} tasks`}
                </Text>
              </YStack>
              <YStack alignItems="end" gapY="xs">
                <XStack alignItems="center">
                  <Text type="h3">
                    {formatCurrencyNatural(selectedPlanPricing?.primaryPrice ?? selectedPlanData.price)}
                  </Text>
                  <Text type="p-sm">/mo</Text>
                </XStack>
                {selectedPlanPricing?.secondaryPrice ? (
                  <Text className="line-through" color="tertiary" type="p-sm">
                    {formatCurrencyNatural(selectedPlanPricing.secondaryPrice)}/mo
                  </Text>
                ) : null}
              </YStack>
            </XStack>
          </YStack>
        ) : purchasePackage.type === 'credits' ? (
          <YStack gap="xs">
            <Text type="p-lg">
              {showCredits ? `Your ${pricingTerminology.unit_term_plural} pack` : 'Your purchase'}
            </Text>
            <XStack
              alignItems="center"
              bg="white"
              borderColor="borderSecondary"
              borderWidth={2}
              className="justify-between"
              gapX="md"
              p="md"
              radius="lg"
            >
              {showCredits ? (
                <YStack>
                  <Text type="h5">
                    {`${formatNumber(purchasePackage.package.amount)} ${pluralize(pricingTerminology.unit_term, purchasePackage.package.amount)}`}
                  </Text>
                  <Text type="p-sm">{purchasePackage.package.description}</Text>
                </YStack>
              ) : (
                <Text type="h5">{formatTaskEstimate(purchasePackage.package.amount)}</Text>
              )}
              <Text type="h3">{formatCurrencyNatural(purchasePackage.package.priceCent)}</Text>
            </XStack>
          </YStack>
        ) : null}
        <YStack gap="xs">
          <Text type="p-lg">Pay with</Text>
          <Text color="tertiary" type="p-sm">
            {`To change or update your default card, please go to settings > payment.`}
          </Text>
        </YStack>
        <YStack gap="sm">
          {isCardsLoading ? (
            <SkeletonList countSkeletons={1} />
          ) : defaultCard ? (
            <XStack className="items-center w-full rounded-lg border justify-between border-neutral-100 p-md">
              <XStack className="items-center gap-sm">
                {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
                <YStack className="h-[18px] w-[27px] items-center">
                  <Image source={resolvePaymentCardBrand(defaultCard.brand, { fallback: Visa })} />
                </YStack>
                <Text type="p-md">**** {defaultCard.lastFour}</Text>
              </XStack>
              <YStack>
                <Text color="tertiary" type="caption">
                  Exp {defaultCard.month}/{defaultCard.year}
                </Text>
              </YStack>
            </XStack>
          ) : (
            <Text>No default card found.</Text>
          )}
        </YStack>
        {promotionDescription && purchasePackage.type === 'subscription' ? (
          <YStack gap="xs">
            <Text type="p-lg">Promotion</Text>
            <XStack
              alignItems="center"
              bg="white"
              borderColor="borderPrimary"
              borderWidth={2}
              gapX="md"
              p="lg"
              radius="lg"
            >
              <Icon color="tertiary" name="IconGift" size={20} />
              <YStack className="flex-1">
                <Text type="h5">{promoCode ? `Promo code: ${promoCode}` : 'Promo applied'}</Text>
                <Text className="italic" color="secondary" type="p-sm">
                  {promotionDescription}
                </Text>
              </YStack>
            </XStack>
          </YStack>
        ) : null}
      </YStack>
      <Divider />
      {onApplePayComplete ? (
        <YStack className="px-md pt-sm" gap="md">
          <StripeProvider expectedElements={[]} options={{ currency: 'usd', mode: 'setup' }}>
            <BuyCreditsExpressCheckout onComplete={onApplePayComplete} />
          </StripeProvider>
        </YStack>
      ) : null}
      <XStack alignItems="center" className="px-md py-sm" gapX="md" justifyContent="between">
        {buyingSubscription ? (
          <XStack alignItems="center" gapX="sm">
            <Icon name="IconCoin" size={24} />
            {paymentSchedule ? (
              <YStack>
                <XStack alignItems="center" gap="md">
                  <Text type="h5">{`What you'll pay today`}</Text>
                  <Text className="italic font-serif" type="h3">
                    {formatCurrencyNatural(paymentSchedule.amountDueToday)}
                  </Text>
                </XStack>
                {paymentSchedule.dueDate ? (
                  <XStack alignItems="center" gap="md">
                    <Text type="h5">{`What you'll pay on ${dayjs(paymentSchedule.dueDate).format('MMMM D, YYYY')}`}</Text>
                    <Text className="italic font-serif" type="h3">
                      {formatCurrencyNatural(price)}
                    </Text>
                  </XStack>
                ) : null}
              </YStack>
            ) : (
              <XStack alignItems="center" gap="md">
                <Text type="h5">{`What you'll pay today`}</Text>
                <Text className="italic font-serif" type="h3">
                  {formatCurrencyNatural(price)}
                </Text>
              </XStack>
            )}
          </XStack>
        ) : null}
        <Button
          disabled={!defaultCard}
          fullWidth={!buyingSubscription}
          label={paymentSchedule ? 'Update plan' : `Pay ${formatCurrencyNatural(price)}`}
          onClick={() => {
            if (!defaultCard) {
              return;
            }
            onSelectCard(defaultCard);
          }}
          size="lg"
          variant="primary"
        />
      </XStack>
    </YStack>
  );
};
